/**
 * The Mac-style desktop's own chrome.
 *
 *   MacMenuBar — the slim bar along the top: the app menu ("Fias"), File,
 *                Edit, View, Go and Window, built by the Desktop from the
 *                same actions its context menu uses; the arrangement's save
 *                status sits at the right, where a Mac shows status items.
 *   MacDock    — the Dock along the bottom, centred: Finder (a Documents
 *                window), every open window (a dot under each), and the
 *                Trash at the right end — on a Mac the Trash lives in the
 *                Dock, not on the desktop, and things are dropped on it
 *                there.
 */
import type { ReactNode } from 'react';
import { Check, Smile, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { TaskbarWindow } from './DesktopTaskbar';

export interface BarMenuEntry {
  label: string;
  onSelect?: () => void;
  shortcut?: string;
  disabled?: boolean;
  checked?: boolean;
  separatorBefore?: boolean;
  /** A submenu instead of an action. */
  items?: BarMenuEntry[];
}

export interface BarMenu {
  label: string;
  /** Drawn bold, like a Mac's app menu. */
  bold?: boolean;
  entries: BarMenuEntry[];
}

function Entries({ entries }: { entries: readonly BarMenuEntry[] }) {
  return (
    <>
      {entries.map((entry, i) => (
        <div key={`${entry.label}-${i}`}>
          {entry.separatorBefore && i > 0 && <DropdownMenuSeparator />}
          {entry.items ? (
            <DropdownMenuSub>
              <DropdownMenuSubTrigger className="text-[13px]">{entry.label}</DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="min-w-[12rem]">
                <Entries entries={entry.items} />
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          ) : (
            <DropdownMenuItem
              disabled={entry.disabled}
              onSelect={() => entry.onSelect?.()}
              className="gap-2 text-[13px]"
            >
              <Check
                className={cn('h-3.5 w-3.5', entry.checked ? 'opacity-100' : 'opacity-0')}
                aria-hidden
              />
              <span className="flex-1">{entry.label}</span>
              {entry.shortcut && <DropdownMenuShortcut>{entry.shortcut}</DropdownMenuShortcut>}
            </DropdownMenuItem>
          )}
        </div>
      ))}
    </>
  );
}

export function MacMenuBar({ menus, status }: { menus: readonly BarMenu[]; status?: ReactNode }) {
  return (
    <div
      role="menubar"
      aria-label="Menu bar"
      className="flex h-7 shrink-0 items-center gap-0.5 border-b border-black/10 bg-background/70 px-2 text-[13px] backdrop-blur-md dark:border-white/10"
    >
      <Smile className="mx-1.5 h-3.5 w-3.5 text-foreground/80" aria-hidden />
      {menus.map((menu) => (
        <DropdownMenu key={menu.label}>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              role="menuitem"
              className={cn(
                'rounded px-2 py-0.5 text-foreground/90 hover:bg-black/10 data-[state=open]:bg-[#0a64d6] data-[state=open]:text-white dark:hover:bg-white/15',
                menu.bold && 'font-semibold',
              )}
            >
              {menu.label}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="min-w-[14rem]">
            <Entries entries={menu.entries} />
          </DropdownMenuContent>
        </DropdownMenu>
      ))}
      <div className="ml-auto flex items-center gap-2 pr-1 text-xs text-muted-foreground">
        {status}
      </div>
    </div>
  );
}

export function MacDock({
  windows,
  onWindow,
  onFinder,
  trashFull,
  trashHover,
  onTrash,
  onTrashMenu,
}: {
  windows: readonly TaskbarWindow[];
  onWindow: (id: string) => void;
  /** Finder: open (or raise) a Documents window. */
  onFinder: () => void;
  trashFull: boolean;
  /** A drag is over the Trash, and whether it would be accepted. */
  trashHover: 'allowed' | 'refused' | null;
  onTrash: () => void;
  onTrashMenu: (pointer: { x: number; y: number }, el: HTMLElement) => void;
}) {
  const tile =
    'relative flex h-11 w-11 items-center justify-center rounded-xl transition-transform hover:-translate-y-1';
  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-2 z-[45] flex justify-center">
      <div
        role="toolbar"
        aria-label="Dock"
        className="pointer-events-auto flex items-end gap-1.5 rounded-2xl border border-white/40 bg-white/55 px-2 py-1.5 shadow-xl backdrop-blur-xl dark:border-white/10 dark:bg-neutral-800/60"
      >
        <button
          type="button"
          onClick={onFinder}
          aria-label="Finder — open Documents"
          title="Finder"
          className={cn(tile, 'bg-gradient-to-b from-sky-300 to-blue-600 text-white shadow')}
        >
          <Smile className="h-7 w-7" aria-hidden />
        </button>
        {windows.length > 0 && (
          <span className="mx-1 h-10 w-px self-center bg-black/15 dark:bg-white/15" />
        )}
        {windows.map((w) => (
          <button
            key={w.id}
            type="button"
            onClick={() => onWindow(w.id)}
            aria-pressed={!w.minimized && w.focused}
            aria-label={w.minimized ? `Restore ${w.title}` : w.title}
            title={w.title}
            className={cn(tile, 'bg-white/80 shadow-sm dark:bg-neutral-700/80')}
          >
            <span className="scale-125">{w.icon}</span>
            <span
              aria-hidden
              className={cn(
                'absolute -bottom-1 h-1 w-1 rounded-full',
                w.minimized ? 'bg-foreground/30' : 'bg-foreground/70',
              )}
            />
          </button>
        ))}
        <span className="mx-1 h-10 w-px self-center bg-black/15 dark:bg-white/15" />
        <button
          type="button"
          data-desktop-drop="trash"
          onClick={onTrash}
          onContextMenu={(e) => {
            e.preventDefault();
            onTrashMenu({ x: e.clientX, y: e.clientY }, e.currentTarget);
          }}
          aria-label={trashFull ? 'Trash (has files)' : 'Trash (empty)'}
          title="Trash"
          className={cn(
            tile,
            'bg-white/80 shadow-sm dark:bg-neutral-700/80',
            trashHover === 'allowed' && '-translate-y-1 ring-2 ring-[#0a64d6]',
            trashHover === 'refused' && 'ring-2 ring-destructive/60',
          )}
        >
          <Trash2 className="h-6 w-6 text-foreground/70" aria-hidden />
          {trashFull && (
            <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-foreground/50" />
          )}
        </button>
      </div>
    </div>
  );
}

/** A row in a Mac window's sidebar. */
export function SidebarItem({
  icon,
  label,
  active,
  onClick,
  drop,
}: {
  icon: ReactNode;
  label: string;
  active: boolean;
  onClick: () => void;
  /** `data-desktop-drop` value, so dragging onto it moves there. */
  drop?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? 'location' : undefined}
      {...(drop ? { 'data-desktop-drop': drop } : {})}
      className={cn(
        'mx-1.5 flex w-[calc(100%-0.75rem)] items-center gap-2 rounded-md px-2 py-1 text-left',
        active
          ? 'bg-black/10 font-medium dark:bg-white/15'
          : 'hover:bg-black/5 dark:hover:bg-white/10',
      )}
    >
      <span className="shrink-0 text-[#0a64d6]">{icon}</span>
      <span className="truncate">{label}</span>
    </button>
  );
}

export function SidebarHeading({ children }: { children: ReactNode }) {
  return (
    <p className="mb-0.5 mt-2 px-3.5 text-[11px] font-semibold text-muted-foreground first:mt-0">
      {children}
    </p>
  );
}
